/**
 * Região (estado e cidade) a partir da coordenada do endereço.
 *
 * O texto de cidade e estado é digitado por quem cadastra, e a mesma cidade
 * aparecia como grupos diferentes: "MG" e "Minas Gerais", "SRS" e "Santa Rita
 * do Sapucaí". A coordenada não tem esse problema. Por isso a região é
 * resolvida assim, nesta ordem:
 *
 * 1. **com coordenada**: o estado é o polígono que contém o ponto, e a cidade é
 *    o município daquele estado com a sede mais próxima;
 * 2. **sem coordenada**: o texto é normalizado ("Minas Gerais", "mg" e "M.G."
 *    viram MG; a cidade é procurada pelo nome, sem acento, entre os municípios
 *    do estado);
 * 3. **sem nada que case**: fica o texto como veio, para o registro não sumir
 *    da contagem.
 *
 * A cidade pela sede mais próxima é uma aproximação: um endereço rural perto
 * da divisa pode ser atribuído ao município vizinho. Para contar onde o Zé
 * Padoca acontece é o bastante, e evita carregar no navegador as fronteiras dos
 * 5.570 municípios.
 *
 * Os dados vêm de `src/data/geo` (ver o README de lá) e são carregados sob
 * demanda, fora do pacote principal.
 */

export interface StateFeature {
  type: "Feature";
  properties: { uf: string; nome: string };
  geometry:
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] };
}

export interface GeoData {
  ufs: { type: "FeatureCollection"; features: StateFeature[] };
  estados: { uf: string; nome: string }[];
  municipios: { rows: [number, string, number, number, string, number][] };
}

export interface Region {
  /** Sigla do estado, ou null quando nem coordenada nem texto o identificam. */
  uf: string | null;
  stateName: string | null;
  /** Chave estável da cidade: o código IBGE, ou o texto normalizado como reserva. */
  cityKey: string | null;
  cityName: string | null;
  /** De onde a região saiu, para a tela poder dizer quando é só o texto digitado. */
  source: "coordenada" | "texto" | "nenhuma";
  /**
   * Onde desenhar o registro num mapa: a própria coordenada, ou a sede do
   * município quando só o texto casou. Null quando não há como localizar.
   */
  latitude: number | null;
  longitude: number | null;
}

export interface RegionResolver {
  resolve(input: {
    latitude?: number | null;
    longitude?: number | null;
    city?: string | null;
    state?: string | null;
  }): Region;
  /** Todos os estados, em ordem alfabética de sigla, para os filtros. */
  states: { uf: string; nome: string }[];
}

interface Municipality {
  ibge: number;
  nome: string;
  lat: number;
  lng: number;
  uf: string;
  key: string;
}

interface PreparedState {
  uf: string;
  nome: string;
  bbox: [number, number, number, number];
  polygons: number[][][][];
}

// Caixa que contém o território brasileiro com folga. Coordenada fora dela é
// lixo de cadastro (o 0,0 de um endereço sem geocodificação, por exemplo).
const BRAZIL_BBOX = { minLat: -34.5, maxLat: 6, minLng: -74.5, maxLng: -28.5 };

/** Sem acento, minúsculo, só letras e números: a forma de comparar texto digitado. */
export function normalizeText(text: string | null | undefined): string {
  return (text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function boundsOf(polygons: number[][][][]): [number, number, number, number] {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const polygon of polygons) {
    for (const [lng, lat] of polygon[0]) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [minLng, minLat, maxLng, maxLat];
}

/** Ponto dentro do anel, pelo método do raio (par-ímpar). */
function insideRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

function insidePolygon(lng: number, lat: number, polygon: number[][][]): boolean {
  if (!insideRing(lng, lat, polygon[0])) return false;
  // Os demais anéis são buracos (o Distrito Federal dentro de Goiás).
  for (let k = 1; k < polygon.length; k += 1) {
    if (insideRing(lng, lat, polygon[k])) return false;
  }
  return true;
}

/** Distância ao quadrado numa projeção local; serve para comparar, não para medir. */
function squaredDistance(lat: number, lng: number, m: Municipality): number {
  const dLat = lat - m.lat;
  const dLng = (lng - m.lng) * Math.cos((lat * Math.PI) / 180);
  return dLat * dLat + dLng * dLng;
}

function isUsableCoordinate(lat: unknown, lng: unknown): lat is number {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return (
    lat >= BRAZIL_BBOX.minLat &&
    lat <= BRAZIL_BBOX.maxLat &&
    lng >= BRAZIL_BBOX.minLng &&
    lng <= BRAZIL_BBOX.maxLng
  );
}

export function createRegionResolver(data: GeoData): RegionResolver {
  const states: PreparedState[] = data.ufs.features.map((feature) => {
    const polygons =
      feature.geometry.type === "Polygon"
        ? [feature.geometry.coordinates]
        : feature.geometry.coordinates;
    return {
      uf: feature.properties.uf,
      nome: feature.properties.nome,
      bbox: boundsOf(polygons),
      polygons,
    };
  });

  const stateName = new Map(data.estados.map((e) => [e.uf, e.nome]));
  // "mg" e "minas gerais" levam ao mesmo estado.
  const stateByText = new Map<string, string>();
  for (const e of data.estados) {
    stateByText.set(normalizeText(e.uf), e.uf);
    stateByText.set(normalizeText(e.nome), e.uf);
  }

  const municipalities: Municipality[] = data.municipios.rows.map(([ibge, nome, lat, lng, uf]) => ({
    ibge,
    nome,
    lat,
    lng,
    uf,
    key: normalizeText(nome),
  }));
  const byState = new Map<string, Municipality[]>();
  for (const m of municipalities) {
    const list = byState.get(m.uf) ?? [];
    list.push(m);
    byState.set(m.uf, list);
  }

  function stateAt(lat: number, lng: number): string | null {
    for (const state of states) {
      const [minLng, minLat, maxLng, maxLat] = state.bbox;
      if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat) continue;
      if (state.polygons.some((polygon) => insidePolygon(lng, lat, polygon))) return state.uf;
    }
    return null;
  }

  function nearest(lat: number, lng: number, candidates: Municipality[]): Municipality | null {
    let best: Municipality | null = null;
    let bestDistance = Infinity;
    for (const m of candidates) {
      const d = squaredDistance(lat, lng, m);
      if (d < bestDistance) {
        bestDistance = d;
        best = m;
      }
    }
    return best;
  }

  function stateFromText(text: string | null | undefined): string | null {
    const key = normalizeText(text);
    if (!key) return null;
    return stateByText.get(key) ?? stateByText.get(key.replace(/\s+/g, "")) ?? null;
  }

  return {
    states: [...data.estados].sort((a, b) => a.uf.localeCompare(b.uf)),
    resolve({ latitude, longitude, city, state }) {
      if (isUsableCoordinate(latitude, longitude)) {
        const lat = latitude;
        const lng = longitude as number;
        // O polígono simplificado pode deixar de fora um ponto colado ao
        // litoral ou à divisa. Aí vale a sede mais próxima do país inteiro.
        const uf = stateAt(lat, lng);
        const town = nearest(lat, lng, uf ? byState.get(uf) ?? municipalities : municipalities);
        if (town) {
          return {
            uf: town.uf,
            stateName: stateName.get(town.uf) ?? town.uf,
            cityKey: String(town.ibge),
            cityName: town.nome,
            source: "coordenada",
            latitude: lat,
            longitude: lng,
          };
        }
      }

      const uf = stateFromText(state);
      const cityText = normalizeText(city);
      if (uf && cityText) {
        const town = (byState.get(uf) ?? []).find((m) => m.key === cityText);
        if (town) {
          return {
            uf,
            stateName: stateName.get(uf) ?? uf,
            cityKey: String(town.ibge),
            cityName: town.nome,
            source: "texto",
            latitude: town.lat,
            longitude: town.lng,
          };
        }
      }

      if (!uf && !cityText) {
        return {
          uf: null,
          stateName: null,
          cityKey: null,
          cityName: null,
          source: "nenhuma",
          latitude: null,
          longitude: null,
        };
      }

      return {
        uf,
        stateName: uf ? stateName.get(uf) ?? uf : null,
        cityKey: cityText ? `texto:${uf ?? ""}:${cityText}` : null,
        cityName: city?.trim() || null,
        source: "texto",
        latitude: null,
        longitude: null,
      };
    },
  };
}

/** "Santa Rita do Sapucaí, MG", ou o que houver. */
export function formatRegion(region: Region | null | undefined): string {
  if (!region) return "-";
  if (region.cityName && region.uf) return `${region.cityName}, ${region.uf}`;
  return region.cityName ?? region.uf ?? "-";
}

let loadingData: Promise<GeoData> | null = null;
let resolverPromise: Promise<RegionResolver> | null = null;

/**
 * Carrega os dados geográficos uma vez por sessão.
 *
 * O `import()` dinâmico deixa os arquivos (cerca de 380 KB) fora do pacote
 * principal: quem não abre uma tela com região não os baixa.
 */
export function loadGeoData(): Promise<GeoData> {
  if (!loadingData) {
    loadingData = Promise.all([
      import("@/data/geo/ufs.json"),
      import("@/data/geo/estados.json"),
      import("@/data/geo/municipios.json"),
    ])
      .then(([ufs, estados, municipios]) => ({
        ufs: ufs.default as unknown as GeoData["ufs"],
        estados: estados.default as GeoData["estados"],
        municipios: municipios.default as unknown as GeoData["municipios"],
      }))
      .catch((error) => {
        // Sem isto, uma falha de rede deixaria a promessa rejeitada guardada
        // para o resto da sessão.
        loadingData = null;
        throw error;
      });
  }
  return loadingData;
}

/** O resolvedor de região, montado uma vez sobre os dados de `loadGeoData`. */
export function loadRegionResolver(): Promise<RegionResolver> {
  if (!resolverPromise) {
    resolverPromise = loadGeoData()
      .then(createRegionResolver)
      .catch((error) => {
        resolverPromise = null;
        throw error;
      });
  }
  return resolverPromise;
}
